
import React from 'react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const GameFAQ = () => {
  return (
    <section id="game-faq" className="py-10">
      <div className="bg-indigo-800 bg-opacity-50 rounded-xl p-8 shadow-xl">
        <h2 className="text-3xl font-bold mb-6 text-center text-yellow-300">Frequently Asked Questions</h2>
        
        <div className="space-y-6">
          <p className="text-lg leading-relaxed text-indigo-100">
            Have questions about <span className="font-semibold text-pink-300">Capybara Clicker 3D</span>? We've compiled answers to the most common questions about this free online game below.
          </p>
          
          <Accordion type="single" collapsible className="w-full mt-8">
            <AccordionItem value="item-1" className="border-b border-purple-700">
              <AccordionTrigger className="text-lg font-semibold text-yellow-200 hover:text-yellow-100 py-4">
                What is Capybara Clicker 3D?
              </AccordionTrigger>
              <AccordionContent className="text-indigo-100 pb-4">
                <p>Capybara Clicker 3D is a free online incremental game where you build a capybara empire by clicking to multiply your capybara population. The game features 3D graphics, various upgrades, customization options, and an evolving environment that changes as you progress. It's designed to be easy to play but offers depth through strategic upgrade choices and customization.</p>
              </AccordionContent>
            </AccordionItem>
            
            <AccordionItem value="item-2" className="border-b border-purple-700">
              <AccordionTrigger className="text-lg font-semibold text-yellow-200 hover:text-yellow-100 py-4">
                How do I play Capybara Clicker 3D?
              </AccordionTrigger>
              <AccordionContent className="text-indigo-100 pb-4">
                <p>Playing Capybara Clicker 3D is simple! Use your left mouse button to click on the screen, which increases your capybara population. As you accumulate capybaras, you'll earn currency that can be spent on upgrades to make your clicking more efficient or generate passive income. You can also customize your capybaras with different outfits and accessories, and modify the environment as you progress.</p>
              </AccordionContent>
            </AccordionItem>
            
            <AccordionItem value="item-3" className="border-b border-purple-700">
              <AccordionTrigger className="text-lg font-semibold text-yellow-200 hover:text-yellow-100 py-4">
                Is Capybara Clicker 3D free to play?
              </AccordionTrigger>
              <AccordionContent className="text-indigo-100 pb-4">
                <p>Yes! Capybara Clicker 3D is completely free to play online. There are no paywalls that restrict your progress in the game. All features, upgrades, and customization options can be unlocked through normal gameplay without spending any money. The game may offer optional in-game purchases for players who want to support the developers or progress faster, but these are never required to enjoy the full game experience.</p>
              </AccordionContent>
            </AccordionItem>
            
            <AccordionItem value="item-4" className="border-b border-purple-700">
              <AccordionTrigger className="text-lg font-semibold text-yellow-200 hover:text-yellow-100 py-4">
                Do I need to download or install anything to play?
              </AccordionTrigger>
              <AccordionContent className="text-indigo-100 pb-4">
                <p>No downloads or installations are required to play Capybara Clicker 3D. The game runs directly in your web browser, making it accessible on virtually any device with internet access. This includes desktops, laptops, tablets, and mobile phones. Just navigate to the game page, and you can start playing immediately without any additional software.</p>
              </AccordionContent>
            </AccordionItem>
            
            <AccordionItem value="item-5" className="border-b border-purple-700">
              <AccordionTrigger className="text-lg font-semibold text-yellow-200 hover:text-yellow-100 py-4">
                What are the system requirements for Capybara Clicker 3D?
              </AccordionTrigger>
              <AccordionContent className="text-indigo-100 pb-4">
                <p>Capybara Clicker 3D is designed to run on most modern devices. For the best experience, we recommend:</p>
                <ul className="list-disc pl-6 mt-2 space-y-1">
                  <li>A modern web browser (Chrome, Firefox, Safari, or Edge)</li>
                  <li>An internet connection</li>
                  <li>At least 1GB of RAM</li>
                  <li>Any device manufactured within the last 5-7 years should be capable of running the game</li>
                </ul>
                <p className="mt-2">If you experience performance issues, try lowering the graphics quality in the game settings.</p>
              </AccordionContent>
            </AccordionItem>
            
            <AccordionItem value="item-6" className="border-b border-purple-700">
              <AccordionTrigger className="text-lg font-semibold text-yellow-200 hover:text-yellow-100 py-4">
                Does the game save my progress?
              </AccordionTrigger>
              <AccordionContent className="text-indigo-100 pb-4">
                <p>Yes, Capybara Clicker 3D automatically saves your progress using browser storage. This means you can close the game and return later to continue from where you left off. For the save feature to work properly:</p>
                <ul className="list-disc pl-6 mt-2 space-y-1">
                  <li>Make sure cookies and local storage are enabled in your browser</li>
                  <li>Avoid clearing your browser data if you want to keep your game progress</li>
                  <li>Consider creating an account (if available) for cloud saves across different devices</li>
                </ul>
              </AccordionContent>
            </AccordionItem>
            
            <AccordionItem value="item-7" className="border-b border-purple-700">
              <AccordionTrigger className="text-lg font-semibold text-yellow-200 hover:text-yellow-100 py-4">
                What makes Capybara Clicker 3D different from other clicker games?
              </AccordionTrigger>
              <AccordionContent className="text-indigo-100 pb-4">
                <p>Capybara Clicker 3D stands out from other clicker games in several ways:</p>
                <ul className="list-disc pl-6 mt-2 space-y-2">
                  <li><span className="font-semibold">3D Graphics</span>: Unlike most clicker games that use 2D interfaces, Capybara Clicker 3D features a fully realized 3D environment that you can navigate and view from different angles.</li>
                  <li><span className="font-semibold">Capybara Theme</span>: The game focuses on these adorable semi-aquatic rodents, giving it a unique and charming theme that stands out from generic clicker games.</li>
                  <li><span className="font-semibold">Environmental Evolution</span>: As you progress, the game environment changes and evolves, providing visual feedback on your achievements.</li>
                  <li><span className="font-semibold">Extensive Customization</span>: The ability to customize your capybaras with different outfits and accessories adds a creative dimension not found in many clicker games.</li>
                  <li><span className="font-semibold">Weather System</span>: The dynamic weather system that affects gameplay and visuals adds another layer of depth to the experience.</li>
                </ul>
              </AccordionContent>
            </AccordionItem>
            
            <AccordionItem value="item-8" className="border-b border-purple-700">
              <AccordionTrigger className="text-lg font-semibold text-yellow-200 hover:text-yellow-100 py-4">
                Is there an end to Capybara Clicker 3D?
              </AccordionTrigger>
              <AccordionContent className="text-indigo-100 pb-4">
                <p>Like most incremental games, Capybara Clicker 3D doesn't have a definitive "end." The game is designed to be played indefinitely, with ever-increasing goals and achievements to unlock. However, there are major milestones and achievements that can be considered significant progression points.</p>
                <p className="mt-2">Some players set personal goals, such as unlocking all upgrades or reaching a specific capybara population number. The developers also regularly add new content to extend the gameplay experience for long-term players.</p>
              </AccordionContent>
            </AccordionItem>
            
            <AccordionItem value="item-9" className="border-b border-purple-700">
              <AccordionTrigger className="text-lg font-semibold text-yellow-200 hover:text-yellow-100 py-4">
                Can I play Capybara Clicker 3D on mobile devices?
              </AccordionTrigger>
              <AccordionContent className="text-indigo-100 pb-4">
                <p>Yes, Capybara Clicker 3D is designed with responsive controls that work on both desktop and mobile devices. On mobile devices, you can tap the screen instead of clicking with a mouse. The game interface automatically adjusts to fit different screen sizes, ensuring a good experience whether you're playing on a phone, tablet, or computer.</p>
                <p className="mt-2">For the best mobile experience, we recommend playing in landscape orientation on a device with a reasonably sized screen.</p>
              </AccordionContent>
            </AccordionItem>
            
            <AccordionItem value="item-10" className="border-b border-purple-700">
              <AccordionTrigger className="text-lg font-semibold text-yellow-200 hover:text-yellow-100 py-4">
                How do I report bugs or give feedback about the game?
              </AccordionTrigger>
              <AccordionContent className="text-indigo-100 pb-4">
                <p>If you encounter any bugs or want to provide feedback about Capybara Clicker 3D, there are several ways to reach out:</p>
                <ul className="list-disc pl-6 mt-2 space-y-1">
                  <li>Use the in-game feedback option if available (usually found in the settings or help menu)</li>
                  <li>Visit the official game website and look for a contact or support page</li>
                  <li>Reach out through the game's official social media channels</li>
                  <li>Email the developers directly (if an email address is provided)</li>
                </ul>
                <p className="mt-2">The developers appreciate player feedback and regularly implement improvements based on community suggestions.</p>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
          
          <p className="text-lg text-indigo-100 mt-8">
            Still have questions about <span className="font-semibold text-pink-300">Capybara Clicker 3D</span>? Feel free to reach out through our contact page. We're always happy to help players enjoy their capybara-clicking adventure to the fullest!
          </p>
        </div>
      </div>
    </section>
  );
};

export default GameFAQ;
